/**
 * Mal kabul taslağı. Satırlar ve fotoğraflar önce birlikte saklanır;
 * stok girişi detay ekranındaki tamamlama adımında yapılır.
 * Para string kalır; satır/fiş toplamları sunucuda hesaplanır.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alan, Buton, Kart, Secim, Uyari } from "@kantin/ui";
import { ROL_ADI, Rol, STOK_YETKILI_ROLLER } from "@kantin/shared";
import type { MalKabulDetayi } from "@kantin/shared";
import { useYetkili } from "../../veri/oturum.js";
import { apiOku, apiYaz, kapsamDisiMi, yetkisizMi } from "./api.js";
import { MalKabulBelgeleri } from "./MalKabulBelgeleri.js";
import { UrunSecimi } from "./UrunSecimi.js";
import type { FisBelgesi } from "./MalKabulBelgeleri.js";
import {
  barkodBul,
  noktaSecenekleri,
  useNoktalar,
  useUrunler,
} from "./kancalar.js";
import type { BarkodSonucu } from "./ortak.js";
import {
  BarkodAlani,
  IslemDurumu,
  Sayfa,
  SayfaBasligi,
  bugun,
  hataMetni,
  miktarBirArtir,
  miktarGecerliMi,
  miktarNormalize,
  paraGecerliMi,
  paraNormalize,
} from "./ortak.js";

interface GirisSatiri {
  urunId: string;
  anahtar: string;
  miktar: string;
  birimAlis: string;
  sonKullanma: string;
  partiKodu: string;
}

/** Fiş başına sunucuyla aynı satır sınırı. */
const SATIR_UST_SINIR = 500;

/**
 * `anahtar`: dizin anahtarı, ortadaki satır silinince kalan satırların alt
 * ağaçlarını kaydırır. Sayaç bileşen içinde yaşar (Sayim.tsx / Transferler.tsx
 * ile aynı kalıp); modül düzeyinde tutulsaydı her render'da boşuna artardı.
 */
function bosSatir(sira: number): GirisSatiri {
  return {
    anahtar: `satir-${sira}`,
    urunId: "",
    miktar: "",
    birimAlis: "",
    sonKullanma: "",
    partiKodu: "",
  };
}

/**
 * "0", "0,000", "0.0" hepsi sıfırdır. Sayıya çevirmeden, yalnızca metin
 * üzerinde bakılır (ADR-03 refleksi miktarda da korunur). Yalnızca
 * miktarGecerliMi(true) olan değerler için çağrılır; eksi işareti zaten
 * biçim doğrulamasından geçemez.
 */
function sifirMi(deger: string): boolean {
  return /^0*([.,]0*)?$/.test(deger.trim());
}

/**
 * Satır DOLU sayılır: kullanıcı ona bir şey yazmıştır. Boş satır masumdur ve
 * sessizce atlanır; dolu ama geçersiz satır ASLA atlanamaz — atlanırsa fişin
 * bir kalemi envantere hiç girmez ve hata aylar sonra "sayım farkı" kılığında
 * ortaya çıkar (KK-507).
 */
function doluMu(s: GirisSatiri): boolean {
  return Boolean(
    s.urunId ||
    s.miktar.trim() ||
    s.birimAlis.trim() ||
    s.sonKullanma ||
    s.partiKodu.trim(),
  );
}

/** Sunucunun tekillik anahtarıyla AYNI biçim: urun_id|son_kullanma|parti_kodu */
function tekillikAnahtari(s: GirisSatiri): string {
  return `${s.urunId}|${s.sonKullanma.trim()}|${s.partiKodu.trim()}`;
}

export function MalKabul() {
  const { id } = useParams();
  const sorgu = useQuery({ queryKey: ["mal-kabul", id], enabled: Boolean(id), queryFn: () => apiOku<MalKabulDetayi>(`/mal-kabul/${id}`) });
  if (id && sorgu.isPending) return <p role="status">Taslak yükleniyor…</p>;
  if (id && sorgu.error) return <IslemDurumu hata={sorgu.error} />;
  if (sorgu.data && sorgu.data.durum !== "taslak") return <Uyari tur="bilgi" baslik="Bu kayıt düzenlenemez"><Link to={`/mal-kabul/${id}`}>Fişi görüntüle</Link></Uyari>;
  return <MalKabulForm key={id ?? "yeni"} taslak={id ? sorgu.data : undefined} />;
}

function MalKabulForm({ taslak }: { taslak?: MalKabulDetayi }) {
  const git = useNavigate();
  // Arka planda yeniden okuma, yerel formun başladığı sürümü ilerletemez.
  const [baslangicSurumu] = useState(taslak?.surum);
  const istemci = useQueryClient();
  const yazabilir = useYetkili(...STOK_YETKILI_ROLLER);

  const noktalar = useNoktalar({ aktif: true });
  const urunler = useUrunler({ aktif: true });

  const [noktaId, setNoktaId] = useState(taslak?.nokta_id ?? "");
  const [firma, setFirma] = useState(taslak?.firma ?? "");
  const [fisTarihi, setFisTarihi] = useState(taslak?.fis_tarihi ?? bugun());
  const [fisNo, setFisNo] = useState(taslak?.fis_no ?? "");
  const [belgeler, setBelgeler] = useState<FisBelgesi[]>(taslak?.belgeler ?? []);
  const [fotoYukleniyor, setFotoYukleniyor] = useState(false);
  const islemAnahtari = useRef(crypto.randomUUID());
  const satirSayaci = useRef(taslak?.satirlar.length || 1);
  const [satirlar, setSatirlar] = useState<GirisSatiri[]>(taslak?.satirlar.length ? taslak.satirlar.map((s, i) => ({
    anahtar: `satir-${i + 1}`, urunId: s.urun_id, miktar: s.miktar, birimAlis: s.birim_alis ?? "",
    sonKullanma: s.son_kullanma ?? "", partiKodu: s.parti_kodu ?? "",
  })) : [bosSatir(1)]);
  useEffect(() => {
    if (!taslak && !noktaId && noktalar.data?.noktalar.length === 1) setNoktaId(noktalar.data.noktalar[0]!.id);
  }, [taslak, noktaId, noktalar.data]);
  const [gonderildi, setGonderildi] = useState(false);

  const urunAra = (id: string) =>
    (urunler.data?.urunler ?? []).find((u) => u.id === id);

  const satirGuncelle = (i: number, yama: Partial<GirisSatiri>) => {
    setSatirlar((o) => o.map((x, j) => (j === i ? { ...x, ...yama } : x)));
  };

  /**
   * KK-503 · barkod okutma. Depoda da kasadakinin aynısı bir USB HID okuyucu
   * vardır; mal kabulde ürünü 500 kalemlik seçim kutusunda aramak, okutmanın
   * yanında hem yavaş hem hatalıdır (yanlış ürün seçmek sessiz bir hatadır).
   *
   * NE YAPAR, NE YAPMAZ:
   *
   * - Okutma yalnızca ÜRÜNÜ ve ADEDİ koyar. Birim alış fiyatını barkod
   *   söylemez — fiyat irsaliyeden okunur ve elle yazılır; tahmin edilirse
   *   maliyet ve kâr raporları sessizce yanlışlanır.
   *
   * - Miktarı yalnızca SKT'si ve parti kodu BOŞ olan satırda artırır. Sunucunun
   *   tekillik anahtarı `urun_id|son_kullanma|parti_kodu` olduğu için partili
   *   satırı büyütmek, başka bir partinin miktarını şişirmek demektir; bu
   *   yüzden partili satır varken okutma YENİ satır açar.
   */
  const barkodOkut = (barkod: string): BarkodSonucu => {
    if (urunler.isPending) {
      return {
        tur: "hata",
        mesaj: "Ürün listesi henüz yüklenmedi; bir an sonra tekrar okutun.",
      };
    }
    const urun = barkodBul(urunler.data?.urunler, barkod);
    if (!urun) {
      return {
        tur: "hata",
        mesaj: `"${barkod}" tanımlı bir ürün barkodu değil. Barkodu Ürünler ekranından ürüne ekleyin; pasif ürünler bu listede yer almaz.`,
      };
    }

    const hedef = satirlar.findIndex(
      (s) =>
        s.urunId === urun.id &&
        s.sonKullanma === "" &&
        s.partiKodu.trim() === "",
    );

    if (hedef >= 0) {
      const mevcut = satirlar[hedef]!;
      const artan = miktarBirArtir(mevcut.miktar);
      if (artan === null) {
        return {
          tur: "hata",
          mesaj: `${urun.ad} satır ${hedef + 1}'de, ama miktarı okunamıyor ("${mevcut.miktar}"). Miktarı düzeltin, sonra okutmaya devam edin.`,
        };
      }
      satirGuncelle(hedef, { miktar: artan });
      return {
        tur: "onay",
        mesaj: `${urun.ad} · satır ${hedef + 1} miktarı: ${artan}`,
      };
    }

    const bosIndeks = satirlar.findIndex((s) => !doluMu(s));
    if (bosIndeks >= 0) {
      satirGuncelle(bosIndeks, { urunId: urun.id, miktar: "1" });
      return {
        tur: "onay",
        mesaj: `${urun.ad} · satır ${bosIndeks + 1}'e eklendi. Birim alış fiyatını yazın.`,
      };
    }

    satirSayaci.current += 1;
    const yeniSatir: GirisSatiri = {
      ...bosSatir(satirSayaci.current),
      urunId: urun.id,
      miktar: "1",
    };
    setSatirlar((o) => [...o, yeniSatir]);
    return {
      tur: "onay",
      mesaj: `${urun.ad} · satır ${satirlar.length + 1} olarak eklendi. Birim alış fiyatını yazın.`,
    };
  };

  // --- satır bazlı doğrulama -------------------------------------------------

  const urunHatasi = (s: GirisSatiri): string | null =>
    !s.urunId && doluMu(s) ? "Bu satırda ürün seçilmedi." : null;

  const miktarHatasi = (s: GirisSatiri): string | null => {
    if (!s.urunId) return null;
    if (!miktarGecerliMi(s.miktar))
      return "Miktarı 5 veya 5,500 biçiminde girin.";
    if (sifirMi(s.miktar)) return "Giriş miktarı sıfırdan büyük olmalıdır.";
    return null;
  };

  const fiyatHatasi = (s: GirisSatiri): string | null => {
    if (!s.urunId || !s.birimAlis.trim()) return null;
    if (!paraGecerliMi(s.birimAlis))
      return "Birim alışı 12 veya 12,50 biçiminde girin.";
    return null;
  };

  const sktHatasi = (s: GirisSatiri): string | null => {
    if (!s.urunId || s.sonKullanma) return null;
    const u = urunAra(s.urunId);
    if (u?.skt_takibi) {
      return "Bu üründe SKT takibi açık; son kullanma tarihi zorunludur (KK-531).";
    }
    return null;
  };

  // Tekrar eden üçlüler: ilk görülen satır da işaretlenir, kullanıcı hangi iki
  // satırı birleştireceğini görsün.
  const tekrarli = new Set<number>();
  const gorulen = new Map<string, number>();
  satirlar.forEach((s, i) => {
    if (!s.urunId) return;
    const anahtar = tekillikAnahtari(s);
    const ilk = gorulen.get(anahtar);
    if (ilk === undefined) gorulen.set(anahtar, i);
    else {
      tekrarli.add(ilk);
      tekrarli.add(i);
    }
  });

  // Gönderim kapısı "en az biri geçerli" DEĞİL, "dolu olan HER satır geçerli"
  // olmalıdır. Aksi halde 3 kalemlik fişin hatalı kalemi sessizce düşer,
  // form sıfırlanır ve kullanıcı 3 kalem girdiğini sanır.
  const doluSatirlar = satirlar.filter(doluMu);
  const satirGecerliMi = (s: GirisSatiri) =>
    s.urunId !== "" &&
    miktarHatasi(s) === null &&
    fiyatHatasi(s) === null;
  const gecerliSatirlar = doluSatirlar.filter(satirGecerliMi);
  const gonderilebilir =
    Boolean(noktaId) &&
    gecerliSatirlar.length === doluSatirlar.length &&
    doluSatirlar.length <= SATIR_UST_SINIR &&
    tekrarli.size === 0;
  const gecmisSkt = satirlar.some(
    (s) => s.sonKullanma && s.sonKullanma < bugun(),
  );

  // --- gönderim --------------------------------------------------------------

  const giris = useMutation({
    mutationFn: () =>
      apiYaz<MalKabulDetayi>(taslak ? "PUT" : "POST", taslak ? `/mal-kabul/${taslak.id}` : "/mal-kabul", {
        ...(taslak ? { surum: baslangicSurumu } : { islem_anahtari: islemAnahtari.current }),
        nokta_id: noktaId,
        firma, fis_tarihi: fisTarihi, fis_no: fisNo,
        belge_idler: belgeler.map((b) => b.medya_id),
        satirlar: gecerliSatirlar.map((s) => ({
          urun_id: s.urunId,
          miktar: miktarNormalize(s.miktar),
          birim_alis: s.birimAlis.trim() ? paraNormalize(s.birimAlis) : null,
          ...(s.sonKullanma ? { son_kullanma: s.sonKullanma } : {}),
          ...(s.partiKodu.trim() ? { parti_kodu: s.partiKodu.trim() } : {}),
        })),
      }),
    onSuccess: (y) => {
      istemci.setQueryData(["mal-kabul", y.id], y);
      void istemci.invalidateQueries({ queryKey: ["mal-kabul-liste"] });
      git(`/mal-kabul/${y.id}`);
    },
  });

  // Nokta kapsamı reddi 404 döner; rol reddiyle birlikte açıklanır.
  const yetkiHatasi =
    giris.error && (yetkisizMi(giris.error) || kapsamDisiMi(giris.error));

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Mal kabul"
        aciklama="Ürün satırlarını ve fiş fotoğraflarını taslağa kaydedin; özeti kontrol edip tamamladığınızda stok artar."
      />
      <Link className="text-sm underline" to="/mal-kabul">Mal kabul geçmişi</Link>

      {!yazabilir && (
        <Uyari tur="bilgi" baslik="Mal kabul yetkiniz yok">
          Stoğa giriş yalnızca <strong>{ROL_ADI[Rol.NOKTA_SORUMLUSU]}</strong>,{" "}
          <strong>{ROL_ADI[Rol.KANTIN_MUDURU]}</strong>,{" "}
          <strong>{ROL_ADI[Rol.OKUL_YONETIMI]}</strong> ve{" "}
          <strong>{ROL_ADI[Rol.OKUL_SISTEM_YONETICISI]}</strong> rolleriyle yapılabilir.
          Formu görebilirsiniz; kayıt sunucuda reddedilir. Yetki değişikliği
          için okul yönetimine başvurun.
        </Uyari>
      )}

      <Kart baslik="Giriş fişi">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setGonderildi(true);
            if (!gonderilebilir || !yazabilir || fotoYukleniyor || giris.isPending) return;
            giris.mutate();
          }}
        >
          <fieldset disabled={!yazabilir || giris.isPending} className="contents">
          <div className="grid gap-3 md:grid-cols-3">
            <Secim
              etiket="Giriş yapılacak nokta"
              value={noktaId}
              required
              disabled={Boolean(taslak)}
              onChange={(e) => setNoktaId(e.target.value)}
              secenekler={noktaSecenekleri(
                noktalar.data?.noktalar,
                "Nokta seçin",
              )}
            />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Alan etiket="Firma" value={firma} onChange={(e) => setFirma(e.target.value)} maxLength={200} />
            <Alan etiket="Fiş tarihi" type="date" required value={fisTarihi} onChange={(e) => setFisTarihi(e.target.value)} />
            <Alan etiket="Fiş numarası (varsa)" value={fisNo} onChange={(e) => setFisNo(e.target.value)} maxLength={100} />
          </div>
          <MalKabulBelgeleri belgeler={belgeler} degistir={setBelgeler} mesgulDegistir={setFotoYukleniyor} />

          <div className="grid gap-3 md:grid-cols-2">
            <BarkodAlani
              onOkut={barkodOkut}
              pasif={urunler.isPending || urunler.isError}
              pasifMetni={urunler.isError ? "Ürün listesi yüklenemedi." : "Ürün listesi yükleniyor…"}
              ipucu="Okutulan ürün satıra eklenir, adedi bir artar. Birim alış fiyatı ve varsa SKT/parti kodu elle girilir. Kısayol: F2."
            />
          </div>

          <IslemDurumu hata={urunler.error} />
          <section aria-label="Giriş satırları">
            <h3 className="m-0 mb-2 text-lg">Satırlar</h3>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {satirlar.map((s, i) => {
                const mHata = gonderildi ? miktarHatasi(s) : null;
                const fHata = gonderildi ? fiyatHatasi(s) : null;
                const sHata = gonderildi ? sktHatasi(s) : null;
                const uHata = gonderildi ? urunHatasi(s) : null;
                return (
                  <li
                    key={s.anahtar}
                    className="flex flex-col gap-1 border-b border-cetvel-silik pb-3 last:border-b-0 last:pb-0"
                  >
                    <div className="grid gap-2 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto]">
                      <UrunSecimi
                        etiket={`Satır ${i + 1} ürün`}
                        deger={s.urunId}
                        degistir={(urunId) =>
                          satirGuncelle(i, { urunId })
                        }
                        urunler={urunler.data?.urunler}
                        pasif={urunler.isPending || urunler.isError}
                      />
                      <Alan
                        etiket={`Satır ${i + 1} miktar`}
                        value={s.miktar}
                        onChange={(e) =>
                          satirGuncelle(i, { miktar: e.target.value })
                        }
                        inputMode="decimal"
                        {...(mHata ? { hata: mHata } : {})}
                      />
                      <Alan
                        etiket={`Satır ${i + 1} birim alış (varsa)`}
                        value={s.birimAlis}
                        onChange={(e) =>
                          satirGuncelle(i, { birimAlis: e.target.value })
                        }
                        inputMode="decimal"
                        {...(fHata ? { hata: fHata } : {})}
                      />
                      <Alan
                        etiket={`Satır ${i + 1} son kullanma`}
                        type="date"
                        value={s.sonKullanma}
                        onChange={(e) =>
                          satirGuncelle(i, { sonKullanma: e.target.value })
                        }
                        {...(sHata ? { hata: sHata } : {})}
                      />
                      <Alan
                        etiket={`Satır ${i + 1} parti kodu`}
                        value={s.partiKodu}
                        onChange={(e) =>
                          satirGuncelle(i, { partiKodu: e.target.value })
                        }
                        maxLength={64}
                      />
                      <div className="flex items-end">
                        <Buton
                          type="button"
                          tur="sessiz"
                          onClick={() =>
                            setSatirlar((o) =>
                              o.length === 1 ? o : o.filter((_, j) => j !== i),
                            )
                          }
                          aria-label={`Satır ${i + 1} kaldır`}
                        >
                          Kaldır
                        </Buton>
                      </div>
                    </div>
                    {uHata && (
                      <p className="m-0 text-xs text-hata" role="alert">
                        {uHata}
                      </p>
                    )}
                    {tekrarli.has(i) && (
                      <p className="m-0 text-xs text-hata" role="alert">
                        Bu satır başka bir satırla aynı ürün, son kullanma ve
                        parti koduna sahip. Aynı parti tek satırda
                        toplanmalıdır; miktarları birleştirin.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-3">
              <Buton
                type="button"
                tur="ikincil"
                onClick={() => {
                  satirSayaci.current += 1;
                  setSatirlar((o) => [...o, bosSatir(satirSayaci.current)]);
                }}
              >
                Satır ekle
              </Buton>
            </div>

            {gonderildi && doluSatirlar.length === 0 && (
              <p className="m-0 mt-2 text-xs text-hata" role="alert">
                Tamamlamak için en az bir satır girin: ürün ve sıfırdan büyük miktar zorunludur.
              </p>
            )}
            {gonderildi &&
              doluSatirlar.length > 0 &&
              gecerliSatirlar.length < doluSatirlar.length && (
                <p className="m-0 mt-2 text-xs text-hata" role="alert">
                  Fişte eksik veya hatalı{" "}
                  <span className="rakam font-mono">
                    {doluSatirlar.length - gecerliSatirlar.length}
                  </span>{" "}
                  satır var. Hatalı satır sessizce atlanmaz — düzeltin veya
                  kaldırın.
                </p>
              )}
            {doluSatirlar.length > SATIR_UST_SINIR && (
              <p className="m-0 mt-2 text-xs text-hata" role="alert">
                Tek fişte en fazla{" "}
                <span className="rakam font-mono">{SATIR_UST_SINIR}</span> satır
                gönderilebilir; fişi bölün.
              </p>
            )}
          </section>

          {gecmisSkt && (
            <Uyari tur="uyari" baslik="Tarihi geçmiş SKT girildi">
              Giren mal <strong>sağlam</strong> kondisyonda kaydedilir; sistem
              tarihi geçmiş partiyi kendiliğinden imha bekliyora ALMAZ. Bu mal
              satılabilir görünür. İmha edilecekse girişten sonra fire/imha
              işlemini ayrıca yapın.
            </Uyari>
          )}

          {yetkiHatasi ? (
            <Uyari tur="hata" baslik="Bu işlemi yapma yetkiniz yok">
              <p className="m-0">
                Sunucu mal kabul isteğini reddetti. Stoğa giriş yalnızca stok
                yetkili rolleriyle ve{" "}
                <strong>yetkili olduğunuz noktalar</strong> için yapılabilir.
                Kendi noktanızı seçin. Yetki eksikse
                okul yönetimine başvurun.
              </p>
              <p className="m-0 mt-2 text-sm">{hataMetni(giris.error)}</p>
            </Uyari>
          ) : (
            <IslemDurumu hata={giris.error} />
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Buton type="submit" tur="birincil" disabled={fotoYukleniyor || !yazabilir} yukleniyor={giris.isPending}>
              Taslağı kaydet ve incele
            </Buton>
            <p className="m-0 text-xs text-murekkep-3">
              Taslak stoğu etkilemez. Tamamlama öncesinde toplamı ve fotoğrafları inceleyebilirsiniz.
            </p>
          </div>
          </fieldset>
        </form>
      </Kart>
    </Sayfa>
  );
}
